import { ArrowUpRight, Phone, PhoneCall, Stethoscope, HeartPulse, ShieldCheck, Target, AlertTriangle } from "lucide-react";

const ELIGIBILITY = [
  "Veterans living in England",
  "People who have served at least one day in the UK Armed Forces",
  "Reservists",
  "Serving personnel who have a confirmed discharge date",
  "People who may have left the Armed Forces many years ago",
];

const PATHWAY = [
  {
    label: "Down",
    body: "Recognise that something isn't where you want it to be.",
    bullets: [],
  },
  {
    label: "Test",
    body: "Look at what's happening.",
    bullets: [
      "Fitness",
      "Nutrition",
      "Sleep",
      "Routine",
      "Confidence",
      "Stress",
      "Work",
      "Relationships",
      "Direction",
    ],
  },
  {
    label: "Adjust",
    body: "Make the appropriate change. Sometimes that's:",
    bullets: [
      "training differently",
      "eating differently",
      "rebuilding routine",
      "setting a new goal",
      "talking to somebody",
      "contacting your GP",
      "accessing specialist veteran support",
    ],
  },
  {
    label: "Move Forward",
    body: "Review what works, keep adjusting and continue moving towards the target.",
    bullets: [],
  },
];

export default function Support() {
  return (
    <div className="bg-[#F4F3EF]" data-testid="support-hub">
      {/* HERO */}
      <section className="bg-[#031D47] text-white py-24 md:py-32 grain relative overflow-hidden">
        <div className="dta-container relative">
          <p className="dta-overline text-white/60">Support hub</p>
          <h1 className="hero-headline text-5xl md:text-7xl lg:text-8xl mt-6 max-w-5xl">
            Sometimes adjusting<br />means asking<br />for support.
          </h1>
          <div className="mt-10 space-y-5 max-w-2xl text-white/75 text-base md:text-lg leading-relaxed">
            <p>
              Down. Test. Adjust. is about recognising where you are, understanding what needs to change and taking positive action.
            </p>
            <p>
              For many people that might mean improving fitness, nutrition, sleep, routine, confidence or simply having some accountability.
            </p>
            <p>
              But sometimes the right adjustment is recognising that you need additional support.
            </p>
            <p className="text-white/60 text-sm md:text-base">
              DTA is not a medical, counselling or mental health treatment service. We will never pretend that training harder, eating better or changing your routine can solve every problem. If you need specialist support, there are organisations specifically designed to help.
            </p>
            <p className="pt-4 font-display uppercase tracking-wide text-white text-lg md:text-xl">
              Asking for support isn't stepping away from the mission. Sometimes it's the adjustment that gets you back on target.
            </p>
          </div>
        </div>
      </section>

      {/* OP COURAGE + OP RESTORE */}
      <section className="py-20 md:py-24 bg-[#F4F3EF]">
        <div className="dta-container grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* OP COURAGE */}
          <article
            data-testid="support-op-courage"
            className="bg-white border border-black/10 p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-[#031D47]" strokeWidth={1.5} />
              <p className="dta-overline text-navy">NHS England — Mental health</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase mt-4 leading-tight">
              Op COURAGE
            </h2>
            <p className="dta-overline text-[0.7rem] text-[#272727] mt-2">
              The Veterans Mental Health and Wellbeing Service
            </p>

            <div className="mt-6 space-y-4 text-[#272727] text-sm md:text-[0.95rem] leading-relaxed">
              <p>
                Op COURAGE is the NHS specialist mental health service for the Armed Forces community in England.
              </p>
              <p>
                It provides specialist support to veterans, reservists and members of the Armed Forces who are approaching discharge and experiencing difficulties with their mental health and wellbeing.
              </p>
              <p>
                Support can include difficulties involving anxiety, low mood, depression, trauma, PTSD, anger, relationships, adjustment to civilian life and other mental health concerns.
              </p>
              <p>
                Op COURAGE understands military life and provides support from professionals experienced in working with the Armed Forces community.
              </p>
            </div>

            <div className="mt-8">
              <p className="dta-overline text-navy">Who can access Op COURAGE?</p>
              <ul className="mt-4 space-y-2">
                {ELIGIBILITY.map((e) => (
                  <li key={e} className="flex items-start gap-3 text-[0.95rem] text-[#272727]">
                    <span className="font-display text-[#031D47] font-bold leading-none pt-1">✓</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 text-sm text-[#272727]/80 leading-relaxed space-y-3 border-t border-black/10 pt-6">
              <p>
                Members normally need to be registered, or eligible to register, with a GP practice in England and will need to provide their military service number.
              </p>
              <p>
                Op COURAGE accepts self-referrals. Referrals can also be made through a GP, healthcare professional, charity, family member or friend.
              </p>
              <p className="italic">
                NHS England confirms that individuals can self-refer to Op COURAGE.
              </p>
            </div>

            <div className="mt-8 flex-1 flex items-end">
              <div className="w-full">
                <a
                  data-testid="support-op-courage-link"
                  href="https://www.nhs.uk/opcourage"
                  target="_blank"
                  rel="noreferrer"
                  className="dta-btn dta-btn-dark inline-flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  Find my local Op COURAGE service
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </a>
                <p className="dta-overline text-[0.65rem] text-navy mt-3">NHS service — England</p>
              </div>
            </div>
          </article>

          {/* OP RESTORE */}
          <article
            data-testid="support-op-restore"
            className="bg-white border border-black/10 p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <HeartPulse className="text-[#031D47]" strokeWidth={1.5} />
              <p className="dta-overline text-navy">NHS — Physical health</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase mt-4 leading-tight">
              Op RESTORE
            </h2>
            <p className="dta-overline text-[0.7rem] text-[#272727] mt-2">
              The Veterans Physical Health and Wellbeing Service
            </p>

            <div className="mt-6 space-y-4 text-[#272727] text-sm md:text-[0.95rem] leading-relaxed">
              <p>
                Some injuries and physical health problems associated with military service can continue long after someone leaves the Armed Forces.
              </p>
              <p>
                Op RESTORE is an NHS service providing specialist care and support for veterans and people approaching discharge who have continuing physical health problems attributable to their Armed Forces service.
              </p>
              <p>
                It brings together NHS clinicians, military-aware specialists and other organisations to help veterans access appropriate treatment and support.
              </p>
            </div>

            <div className="mt-8 border border-black/10 bg-[#F4F3EF] p-5">
              <p className="dta-overline text-navy">Important</p>
              <p className="mt-2 text-sm text-[#272727] leading-relaxed">
                Unlike Op COURAGE, referral to Op RESTORE normally needs to be made by a clinician from the individual's GP practice.
              </p>
            </div>

            <div className="mt-8 flex-1 flex items-end">
              <div className="w-full">
                <a
                  data-testid="support-op-restore-link"
                  href="https://www.oprestore.nhs.uk/"
                  target="_blank"
                  rel="noreferrer"
                  className="dta-btn dta-btn-dark inline-flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  Learn about Op RESTORE
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </a>
                <p className="dta-overline text-[0.65rem] text-navy mt-3">NHS service</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* TELL YOUR GP + COMBAT STRESS */}
      <section className="pb-20 md:pb-24 bg-[#F4F3EF]">
        <div className="dta-container grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* GP */}
          <article
            data-testid="support-gp"
            className="bg-white border border-black/10 p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <Stethoscope className="text-[#031D47]" strokeWidth={1.5} />
              <p className="dta-overline text-navy">Primary care</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase mt-4 leading-tight">
              Tell your GP<br />you served
            </h2>
            <div className="mt-6 space-y-4 text-[#272727] text-sm md:text-[0.95rem] leading-relaxed">
              <p>
                If you have served in the UK Armed Forces, tell your GP practice.
              </p>
              <p>
                Having your veteran status recorded can help your GP understand whether your health condition may be connected to your military service and whether specialist veteran services could be appropriate.
              </p>
              <p>
                The NHS encourages veterans to tell their GP that they have served in the Armed Forces.
              </p>
            </div>
            <div className="mt-8 flex-1 flex items-end">
              <a
                data-testid="support-gp-link"
                href="https://www.nhs.uk/nhs-services/armed-forces-community/"
                target="_blank"
                rel="noreferrer"
                className="dta-btn dta-btn-outline-dark inline-flex items-center justify-center gap-2 w-full md:w-auto"
              >
                Find NHS veteran health support
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </a>
            </div>
          </article>

          {/* COMBAT STRESS */}
          <article
            data-testid="support-combat-stress"
            className="bg-white border border-black/10 p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <PhoneCall className="text-[#031D47]" strokeWidth={1.5} />
              <p className="dta-overline text-navy">Need to talk to someone?</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase mt-4 leading-tight">
              Combat Stress
            </h2>
            <p className="dta-overline text-[0.7rem] text-[#272727] mt-2">
              Veterans Mental Health Helpline
            </p>
            <div className="mt-6 space-y-4 text-[#272727] text-sm md:text-[0.95rem] leading-relaxed">
              <p>
                Confidential mental health support for veterans and their families.
              </p>
              <p className="font-display uppercase text-2xl md:text-3xl text-[#031D47] tracking-wide">
                0800 138 1619
              </p>
              <p className="text-sm text-[#272727]/80">
                Available 24 hours a day, 365 days a year according to current NHS guidance.
              </p>
            </div>
            <div className="mt-8 flex-1 flex items-end">
              <a
                data-testid="support-combat-stress-call"
                href="tel:08001381619"
                className="dta-btn dta-btn-dark inline-flex items-center justify-center gap-2 w-full md:w-auto"
              >
                <Phone className="w-4 h-4" strokeWidth={1.75} />
                Call 0800 138 1619
              </a>
            </div>
          </article>
        </div>
      </section>

      {/* WHERE DTA FITS */}
      <section className="bg-[#0A0A0A] text-white py-20 md:py-28 grain relative overflow-hidden">
        <div className="dta-container">
          <div className="flex items-center gap-3">
            <Target className="text-[#8fa8d6]" strokeWidth={1.5} />
            <p className="dta-overline text-white/60">The DTA pathway</p>
          </div>
          <h2 className="section-heading text-4xl md:text-6xl mt-5 max-w-3xl">
            Where DTA fits.
          </h2>

          {/* Pathway visual */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-5">
            {PATHWAY.map((step, i) => (
              <div
                key={step.label}
                className="border-t border-white/20 pt-6 relative"
              >
                <span className="dta-overline text-white/40 text-[0.65rem]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display uppercase text-2xl md:text-3xl font-bold mt-2 text-white">
                  {step.label}
                </h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  {step.body}
                </p>
                {step.bullets.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {step.bullets.map((b) => (
                      <li
                        key={b}
                        className="text-[0.8rem] text-white/60 leading-relaxed before:content-['—'] before:mr-2 before:text-white/30"
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 border-t border-white/15 pt-8 max-w-3xl">
            <p className="font-display uppercase text-lg md:text-xl text-white/90 leading-snug">
              DTA can support the lifestyle. Specialist services support the areas requiring specialist care.
            </p>
          </div>
        </div>
      </section>

      {/* URGENT SUPPORT */}
      <section className="py-20 md:py-24 bg-[#F4F3EF] border-b border-black/10">
        <div className="dta-container">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-[#031D47]" strokeWidth={1.5} />
            <p className="dta-overline text-navy">Urgent support</p>
          </div>
          <h2 className="section-heading text-3xl md:text-5xl mt-4 max-w-3xl">
            Need urgent help?
          </h2>
          <p className="mt-5 text-[#272727] max-w-2xl text-base leading-relaxed">
            If you need urgent help for your mental or physical health, contact NHS 111. NHS 111 operates 24 hours a day, seven days a week and can help direct people to appropriate urgent physical or mental health support.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            <a
              data-testid="support-call-111"
              href="tel:111"
              className="dta-btn dta-btn-dark inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" strokeWidth={1.75} />
              Call 111
            </a>
            <a
              data-testid="support-nhs-111-online"
              href="https://111.nhs.uk/"
              target="_blank"
              rel="noreferrer"
              className="dta-btn dta-btn-outline-dark inline-flex items-center justify-center gap-2"
            >
              Visit NHS 111 online
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>

          {/* Immediate danger */}
          <div
            data-testid="support-immediate-danger"
            className="mt-10 border-2 border-[#031D47] bg-white p-6 md:p-8 max-w-3xl"
          >
            <p className="dta-overline text-navy">Immediate danger</p>
            <h3 className="font-display uppercase text-2xl md:text-3xl font-bold mt-2 text-[#031D47] leading-tight">
              If you or somebody else is in<br />immediate danger.
            </h3>
            <p className="mt-4 text-[#272727] text-sm md:text-base">
              If there is a life-threatening emergency, call 999 or go to A&amp;E.
            </p>
            <div className="mt-6">
              <a
                data-testid="support-call-999"
                href="tel:999"
                className="dta-btn dta-btn-dark inline-flex items-center justify-center gap-2 w-full md:w-auto"
              >
                <Phone className="w-4 h-4" strokeWidth={1.75} />
                Call 999
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="py-20 bg-[#F4F3EF]">
        <div className="dta-container">
          <div
            data-testid="support-disclaimer"
            className="border border-black/15 bg-white p-8 md:p-12 max-w-4xl"
          >
            <p className="dta-overline text-navy">Important</p>
            <h3 className="font-display uppercase text-2xl md:text-3xl font-bold mt-3 leading-tight">
              DTA is not a medical<br />or clinical service.
            </h3>
            <div className="mt-6 space-y-4 text-[#272727] text-sm md:text-[0.95rem] leading-relaxed">
              <p>
                Down Test Adjust is a fitness, lifestyle, wellbeing and personal development platform.
              </p>
              <p>
                DTA does not provide medical diagnosis, psychological therapy, psychiatric treatment, counselling or emergency mental health services.
              </p>
              <p>
                Information and links provided within the DTA Support Hub are for information and signposting purposes.
              </p>
              <p>
                If you are concerned about your physical or mental health, speak to an appropriately qualified healthcare professional.
              </p>
              <p className="font-display uppercase text-navy text-base md:text-lg tracking-wide">
                If you require urgent assistance, contact NHS 111. In an emergency or where somebody is in immediate danger, call 999.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL DTA MANTRA */}
      <section className="bg-[#031D47] text-white py-24 md:py-32 grain relative overflow-hidden">
        <div className="dta-container text-center max-w-4xl mx-auto">
          <p className="dta-overline text-white/50">The DTA message</p>
          <h2 className="hero-headline text-5xl md:text-7xl lg:text-8xl mt-6 leading-[0.95]">
            Down.<br />Test.<br />Adjust.
          </h2>
          <p className="mt-10 font-display uppercase tracking-wide text-white/80 text-base md:text-lg">
            Recognise it. Assess it. Make the adjustment. Keep moving forward.
          </p>
          <p className="mt-10 text-white/60 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            DTA isn't here to replace professional support. We're here to help you recognise when an adjustment needs to be made — and sometimes that adjustment is asking the right people for help.
          </p>
        </div>
      </section>
    </div>
  );
}
